import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertTriangle, CheckCircle, XCircle, MinusCircle, Edit, User, Calendar, FileText } from 'lucide-react';
import { toast } from 'sonner';

export default function ISO27001NonCompliantPage() {
  const [controls, setControls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingControl, setEditingControl] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterOwner, setFilterOwner] = useState('all');
  const [sortBy, setSortBy] = useState('due_date');

  useEffect(() => {
    fetchNonCompliantControls();
  }, []);

  const fetchNonCompliantControls = async () => {
    try {
      const response = await axios.get(`${API}/controls?framework=ISO27001`);
      // Filter only non-compliant and partially compliant
      const nonCompliant = response.data.filter(c => 
        c.status === 'No' || c.status === 'Partially' || !c.status
      );
      setControls(nonCompliant);
    } catch (error) {
      toast.error('Failed to fetch controls');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingControl) return;

    try {
      await axios.patch(`${API}/controls/${editingControl.id}`, {
        status: editingControl.status,
        owner: editingControl.owner,
        due_date: editingControl.due_date,
        remediation_status: editingControl.remediation_status,
        action_plan: editingControl.action_plan,
        evidence: editingControl.evidence,
        comments: editingControl.comments
      });
      toast.success('Control updated successfully');
      setShowDialog(false);
      setEditingControl(null);
      fetchNonCompliantControls();
    } catch (error) {
      toast.error('Failed to update control');
    }
  };

  const handleStatusChange = async (controlId, newStatus) => {
    try {
      await axios.patch(`${API}/controls/${controlId}`, {
        status: newStatus
      });
      toast.success('Status updated successfully');
      fetchNonCompliantControls();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Yes':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'No':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'Partially':
        return <MinusCircle className="w-5 h-5 text-slate-600" />;
      default:
        return <MinusCircle className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Yes':
        return 'bg-slate-100 text-green-800';
      case 'No':
        return 'bg-slate-100 text-red-800';
      case 'Partially':
        return 'bg-slate-100 text-slate-800';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const generateRecommendation = (control) => {
    const recommendations = {
      'A.5.1': 'Develop and formalize an Information Security Policy that is approved by management, published within the organization, and effectively communicated to all employees and relevant external parties to ensure awareness, accountability, and compliance with security objectives.',
      'A.5.2': 'Define and communicate Information Security roles and responsibilities across the organization.',
      'A.5.3': 'Segregate duties and responsibilities to prevent unauthorized access or misuse.',
      'A.5.4': 'Ensure management assigns and supports ISMS responsibilities effectively.',
      'A.5.5': 'Establish and maintain appropriate contacts with relevant authorities.',
      'A.5.6': 'Maintain active communication with special interest groups for information sharing and collaboration.',
      'A.5.7': 'Establish a formal Threat Intelligence process to collect, analyze, and act on emerging threats, ensuring proactive defense and informed decision-making.',
      'A.5.8': 'Integrate information security considerations into all project management phases to ensure risks are identified and mitigated early.',
      'A.5.9': 'Develop and maintain a comprehensive hardware and software asset inventory to ensure accurate tracking and protection of organizational assets.',
      'A.5.10': 'Define, document, and enforce an Acceptable Use Policy (AUP) to guide proper and secure use of organizational systems and resources.',
      'A.5.11': 'Implement formal procedures for the timely return of company assets upon employee exit or when no longer required.',
      'A.5.12': 'Establish an information classification framework to categorize data based on sensitivity, value, and regulatory requirements.',
      'A.5.13': 'Implement a consistent information labelling process aligned with the defined classification levels to ensure proper handling of data.',
      'A.5.14': 'Adopt secure communication and transfer mechanisms to protect data shared within and outside the organization.',
      'A.5.15': 'Implement access control measures that align with business needs and the principle of least privilege to prevent unauthorized access.',
      'A.5.16': 'Establish a robust identity management system to ensure secure user identification, authentication, and access provisioning.',
      'A.5.17': 'Implement strong multi-factor authentication controls to ensure secure user access and prevent unauthorized system entry.',
      'A.5.18': 'Establish and enforce a formal user access management process for granting, modifying, and revoking access, including privileged accounts and leavers.',
      'A.5.19': 'Maintain an up-to-date supplier inventory detailing services, risk levels, and criticality to enable effective supply chain risk management.',
      'A.5.20': 'Include comprehensive cybersecurity clauses in supplier contracts covering incident notification, right to audit, SLAs, and data protection requirements.',
      'A.5.21': 'Implement controls to manage and monitor information security across the ICT supply chain, ensuring compliance and reducing third-party risks.',
      'A.5.22': 'Regularly monitor, review, and assess supplier services to ensure continued compliance with security requirements and manage any changes effectively.',
      'A.5.23': 'Establish and enforce robust cybersecurity controls for cloud services, including encryption, access management, monitoring, and compliance checks.',
      'A.5.24': 'Develop and maintain an Incident Response Plan defining roles, responsibilities, and procedures, and test it annually to ensure readiness.',
      'A.5.25': 'Implement a structured process for assessing and responding to information security events based on their impact and severity.',
      'A.5.26': 'Ensure all security incidents are logged and managed through a defined process covering detection, containment, eradication, recovery, and lessons learned.',
      'A.5.27': 'Establish a post-incident review process to capture lessons learned and apply improvements to prevent recurrence.',
      'A.5.28': 'Develop procedures for secure evidence collection and preservation to support investigations and maintain integrity.',
      'A.5.29': 'Implement measures to ensure information security is sustained during disruptions through resilient systems and secure recovery mechanisms.',
      'A.5.30': 'Establish and maintain a Business Continuity Policy and Disaster Recovery Plan, and conduct annual testing to validate effectiveness.',
      'A.5.31': 'Identify and comply with all applicable legal, statutory, regulatory, and contractual information security requirements to avoid non-compliance risks.',
      'A.5.32': 'Implement measures to identify, protect, and respect intellectual property rights to prevent misuse or infringement.',
      'A.5.33': 'Ensure organizational records are securely maintained and protected against loss, unauthorized access, or alteration.',
      'A.5.34': 'Establish processes to ensure privacy and protection of personally identifiable information (PII) in compliance with applicable data protection laws.',
      'A.5.35': 'Conduct regular independent reviews of the information security management system to verify effectiveness and identify areas for improvement.',
      'A.5.36': 'Implement regular compliance checks to ensure adherence to established policies, rules, and standards.',
      'A.5.37': 'Develop, document, and maintain operating procedures to ensure consistent and secure execution of operational activities.',
      'A.6.1': 'Perform comprehensive background verification checks on all candidates before employment to reduce insider risk.',
      'A.6.2': 'Include clear cybersecurity roles and responsibilities within employment agreements to establish accountability.',
      'A.6.3': 'Implement regular cybersecurity awareness and training programs for employees and contractors to strengthen the organization\'s security posture.',
      'A.6.4': 'Establish a disciplinary process to address cybersecurity breaches and enforce policy compliance.',
      'A.6.5': 'Develop a formal employee exit process ensuring return of assets, access revocation, and proper handover of responsibilities.',
      'A.6.6': 'Require all employees and contractors to sign confidentiality or non-disclosure agreements to protect sensitive organizational information.',
      'A.6.7': 'Implement secure remote working controls, including VPN, MFA, and device management, to protect organizational data.',
      'A.6.8': 'Establish clear reporting procedures and train employees to report security incidents immediately.',
      'A.7.1': 'Implement physical access controls such as ID badges, surveillance, and visitor logs to secure facilities.',
      'A.7.2': 'Enforce prompt revocation of physical access upon role changes or termination through coordination with HR and security teams.',
      'A.7.3': 'Install locks, access control systems, and surveillance to prevent unauthorized entry.',
      'A.7.4': 'Implement continuous physical monitoring through CCTV and alarm systems to detect suspicious activities.',
      'A.7.5': 'Implement controls such as fire suppression, flood protection, and climate control to safeguard infrastructure.',
      'A.7.6': 'Strengthen environmental security measures to prevent damage from fire, water, or power failures.',
      'A.7.7': 'Establish and enforce a clear desk and screen policy to prevent unauthorized access to sensitive information.',
      'A.7.8': 'Position and secure equipment to minimize physical and environmental risks.',
      'A.7.9': 'Implement tracking and protection controls for assets used off-site to prevent theft or misuse.',
      'A.7.10': 'Establish secure storage, handling, and disposal procedures for all media types.',
      'A.7.11': 'Maintain redundant and secure utilities to ensure uninterrupted operations.',
      'A.7.12': 'Secure network and power cabling to prevent interception and physical damage.',
      'A.7.13': 'Schedule regular maintenance to preserve system functionality and security.',
      'A.7.14': 'Implement certified data destruction and secure equipment disposal processes.',
      'A.8.1': 'Implement endpoint protection solutions and device management policies to secure all user devices.',
      'A.8.2': 'Enforce strict privileged access controls and perform regular access reviews to prevent misuse.',
      'A.8.3': 'Apply the principle of least privilege across all systems to limit access to only what is necessary.',
      'A.8.4': 'Restrict source code access to authorized personnel and monitor for unauthorized modifications.',
      'A.8.5': 'Implement strong, multi-factor authentication across all systems to enhance account security.',
      'A.8.6': 'Establish capacity management processes to monitor and optimize system performance.',
      'A.8.7': 'Deploy and maintain updated anti-malware tools with real-time scanning and regular updates.',
      'A.8.8': 'Implement a formal vulnerability management program including regular scanning and timely patching.',
      'A.8.9': 'Establish standardized configuration baselines and monitor for unauthorized changes.',
      'A.8.10': 'Implement secure data deletion procedures to prevent recovery of obsolete or sensitive data.',
      'A.8.11': 'Apply data masking and anonymization controls to safeguard sensitive data in testing environments.',
      'A.8.12': 'Deploy DLP solutions to monitor, detect, and block unauthorized data transfers.',
      'A.8.13': 'Establish and test regular backup and restoration procedures to ensure data availability and integrity.',
      'A.8.14': 'Implement redundant systems and failover mechanisms to maintain business continuity.',
      'A.8.15': 'Enable centralized logging and ensure logs are securely stored and retained for at least one year.',
      'A.8.16': 'Implement a SIEM solution to detect, correlate, and alert on potential security events.',
      'A.8.17': 'Ensure all critical systems synchronize with a reliable time source to maintain consistent timestamps.',
      'A.8.18': 'Restrict and monitor privileged utilities to prevent unauthorized access or misuse.',
      'A.8.19': 'Implement application whitelisting and restrict local administrative rights to prevent unauthorized installations.',
      'A.8.20': 'Deploy robust network security controls such as firewalls, IDS/IPS, and segmentation to protect infrastructure.',
      'A.8.21': 'Implement encryption and secure communication protocols (e.g., TLS, VPN) to protect data in transit and ensure network integrity.',
      'A.8.22': 'Segment networks to separate sensitive systems and minimize the impact of potential breaches.',
      'A.8.23': 'Deploy and configure web filtering tools to block malicious or unauthorized websites and reduce exposure to online threats.',
      'A.8.24': 'Implement approved cryptographic algorithms to secure data confidentiality, integrity, and authenticity.',
      'A.8.25': 'Establish a formal key management process to securely generate, distribute, store, rotate, and dispose of cryptographic keys.',
      'A.8.26': 'Adopt a secure SDLC framework integrating security reviews, code analysis, and vulnerability assessments in every stage of development.',
      'A.8.27': 'Define and enforce application security requirements early in development to prevent vulnerabilities and ensure compliance.',
      'A.8.28': 'Apply secure design and architecture principles to enhance resilience and reduce attack surfaces in systems.',
      'A.8.29': 'Develop and approve a Secure Development Policy outlining security responsibilities, coding standards, and testing practices.',
      'A.8.30': 'Conduct regular code reviews, vulnerability scans, and penetration testing before system deployment to detect and fix security issues.',
      'A.8.31': 'Establish contractual security clauses and monitor third-party developers for compliance with security standards.',
      'A.8.32': 'Isolate development, testing, and production environments to prevent unauthorized changes and protect live systems.',
      'A.8.33': 'Use anonymized or synthetic test data and restrict access to protect sensitive information during testing.',
      'A.8.34': 'Implement safeguards such as backups and access restrictions to prevent system disruption or data compromise during testing or audits.',
    };

    for (const [key, value] of Object.entries(recommendations)) {
      if (control.control_id === key) {
        return value;
      }
    }

    if (control.category?.includes('Access')) {
      return 'Implement strong access control mechanisms. Apply least privilege principle, conduct quarterly access reviews, and enforce role-based access control (RBAC).';
    }
    if (control.category?.includes('Cryptographic') || control.category?.includes('Encryption')) {
      return 'Deploy industry-standard encryption controls. Use AES-256 for data at rest, TLS 1.2+ for data in transit, and implement proper key management.';
    }
    if (control.category?.includes('Monitoring') || control.category?.includes('Logging')) {
      return 'Establish comprehensive monitoring infrastructure. Implement centralized logging (SIEM), configure real-time alerts for security events, and retain logs for minimum 1 year.';
    }
    if (control.category?.includes('Physical')) {
      return 'Strengthen physical security controls. Implement access card systems, visitor management, CCTV surveillance, and secure disposal procedures for sensitive materials.';
    }

    return control.action_plan || 'Develop and implement appropriate safeguards, document evidence of implementation, and conduct testing to verify effectiveness.';
  };

  const owners = ['all', ...new Set(controls.map(c => c.owner).filter(Boolean))];

  const filteredControls = controls
    .filter(control => {
      const matchesSearch = control.control_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        control.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (control.owner && control.owner.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesOwner = filterOwner === 'all' || control.owner === filterOwner;
      return matchesSearch && matchesOwner;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'control_id':
          return a.control_id.localeCompare(b.control_id);
        case 'owner':
          return (a.owner || 'zzz').localeCompare(b.owner || 'zzz');
        case 'due_date':
          const dateA = a.due_date ? new Date(a.due_date).getTime() : Infinity;
          const dateB = b.due_date ? new Date(b.due_date).getTime() : Infinity;
          return dateA - dateB;
        case 'status':
          const statusOrder = { 'No': 1, 'Partially': 2, '': 3 };
          return (statusOrder[a.status] || 4) - (statusOrder[b.status] || 4);
        default:
          return 0;
      }
    });

  const stats = {
    total: controls.length,
    critical: controls.filter(c => c.status === 'No').length,
    partial: controls.filter(c => c.status === 'Partially').length,
    overdue: controls.filter(c => c.due_date && new Date(c.due_date) < new Date() && (c.status === 'No' || c.status === 'Partially')).length,
    unassigned: controls.filter(c => !c.owner).length
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <AlertTriangle className="w-8 h-8 text-red-600" />
          <h1 className="text-3xl font-heading font-bold">ISO 27001 - Non-Compliant Controls</h1>
        </div>
        <p className="text-gray-700">
          Priority action items requiring remediation with recommendations and tracking
        </p>
      </div>

      {/* Alert Banner */}
      {stats.critical > 0 && (
        <div className="bg-slate-50 border border-slate-700 rounded-sm p-4">
          <div className="flex items-start gap-3">
            <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-900">Critical Non-Compliant Controls</h3>
              <p className="text-sm text-red-700 mt-1">
                {stats.critical} control{stats.critical !== 1 ? 's are' : ' is'} non-compliant and require{stats.critical === 1 ? 's' : ''} immediate remediation.
                {stats.overdue > 0 && ` ${stats.overdue} control${stats.overdue !== 1 ? 's are' : ' is'} overdue.`}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white border border-border rounded-sm p-4">
          <p className="text-sm text-gray-700">Total Issues</p>
          <p className="text-2xl font-bold mt-1">{stats.total}</p>
        </div>
        <div className="bg-white border border-slate-700 rounded-sm p-4">
          <p className="text-sm text-gray-700">Non-Compliant</p>
          <p className="text-2xl font-bold mt-1 text-red-600">{stats.critical}</p>
        </div>
        <div className="bg-white border border-slate-700 rounded-sm p-4">
          <p className="text-sm text-gray-700">Partially Compliant</p>
          <p className="text-2xl font-bold mt-1 text-slate-600">{stats.partial}</p>
        </div>
        <div className="bg-white border border-slate-700 rounded-sm p-4">
          <p className="text-sm text-gray-700">Overdue</p>
          <p className="text-2xl font-bold mt-1 text-slate-600">{stats.overdue}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-sm p-4">
          <p className="text-sm text-gray-700">Unassigned</p>
          <p className="text-2xl font-bold mt-1 text-gray-600">{stats.unassigned}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-border rounded-sm p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-sm font-medium">Search</Label>
            <Input
              placeholder="Control ID, title, owner..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-sm font-medium">Filter by Owner</Label>
            <select
              value={filterOwner}
              onChange={(e) => setFilterOwner(e.target.value)}
              className="w-full border border-input rounded-sm px-3 py-2 text-sm mt-1 bg-white"
            >
              <option value="all">All Owners</option>
              {owners.filter(o => o !== 'all').map(owner => (
                <option key={owner} value={owner}>{owner}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-sm font-medium">Sort By</Label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full border border-input rounded-sm px-3 py-2 text-sm mt-1 bg-white"
            >
              <option value="due_date">Due Date (Urgent First)</option>
              <option value="status">Status (Critical First)</option>
              <option value="control_id">Control ID</option>
              <option value="owner">Owner</option>
            </select>
          </div>
        </div>
      </div>

      {/* Controls List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="text-lg text-gray-700">Loading controls...</div>
        </div>
      ) : filteredControls.length === 0 ? (
        <div className="bg-white border border-border rounded-sm p-12 text-center">
          <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">All Controls Compliant!</h3>
          <p className="text-gray-700">
            No non-compliant controls found. Great work!
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredControls.map((control) => (
            <div
              key={control.id}
              className="bg-white border-l-4 border-l-red-500 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="p-6">
                {/* Header Row */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-sm font-mono font-bold text-gray-900 bg-gray-100 px-3 py-1 rounded">
                        {control.control_id}
                      </span>
                      <div className="flex items-center gap-2">
                        {getStatusIcon(control.status)}
                        <select
                          value={control.status || ''}
                          onChange={(e) => handleStatusChange(control.id, e.target.value)}
                          className={`px-3 py-1 rounded-sm text-xs font-semibold border-0 cursor-pointer ${getStatusColor(control.status)}`}
                        >
                          <option value="">Select Status</option>
                          <option value="Yes">✓ Compliant</option>
                          <option value="No">✗ Non-Compliant</option>
                          <option value="Partially">◐ Partially</option>
                          <option value="N.A">Not Applicable</option>
                        </select>
                      </div>
                      {control.remediation_status && (
                        <span className={`px-2 py-1 rounded-sm text-xs font-medium ${
                          control.remediation_status === 'In Progress' ? 'bg-slate-100 text-slate-800' :
                          control.remediation_status === 'Blocked' ? 'bg-slate-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {control.remediation_status}
                        </span>
                      )}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{control.title}</h3>
                    <p className="text-sm text-gray-600">{control.requirement}</p>
                  </div>
                </div>

                {/* Info Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 p-4 bg-gray-50 rounded-sm">
                  <div className="flex items-start gap-2">
                    <User className="w-4 h-4 text-gray-500 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Owner</p>
                      <p className="text-sm font-medium text-gray-900">
                        {control.owner || <span className="text-gray-400 italic">Unassigned</span>}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Calendar className="w-4 h-4 text-gray-500 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Closing Date</p>
                      <p className={`text-sm font-medium ${
                        control.due_date && new Date(control.due_date) < new Date() && (control.status === 'No' || control.status === 'Partially')
                          ? 'text-red-600 font-bold' 
                          : 'text-gray-900'
                      }`}>
                        {control.due_date ? new Date(control.due_date).toLocaleDateString() : <span className="text-gray-400 italic">Not set</span>}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <FileText className="w-4 h-4 text-gray-500 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Category</p>
                      <p className="text-sm font-medium text-gray-900">{control.category}</p>
                    </div>
                  </div>
                </div>

                {/* Recommendation Box */}
                <div className="bg-slate-50 border border-slate-700 rounded-sm p-4 mb-4">
                  <div className="flex items-start gap-2">
                    <div className="p-1 bg-slate-100 rounded">
                      <FileText className="w-4 h-4 text-slate-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs font-semibold text-slate-900 mb-1">RECOMMENDED ACTION:</p>
                      <p className="text-sm text-slate-800">{generateRecommendation(control)}</p>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2">
                  <Dialog open={showDialog && editingControl?.id === control.id} onOpenChange={(open) => {
                    setShowDialog(open);
                    if (!open) setEditingControl(null);
                  }}>
                    <DialogTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingControl(control)}
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Update Details
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>Update Control: {control.control_id}</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleUpdate} className="space-y-4">
                        <div>
                          <Label>Control Title</Label>
                          <Input value={control.title} disabled className="mt-1" />
                        </div>

                        <div>
                          <Label>Status</Label>
                          <select
                            value={editingControl?.status || ''}
                            onChange={(e) => setEditingControl({ ...editingControl, status: e.target.value })}
                            className="w-full border border-input rounded-sm px-3 py-2 text-sm mt-1"
                            required
                          >
                            <option value="">Select Status</option>
                            <option value="Yes">Yes - Compliant</option>
                            <option value="No">No - Non-Compliant</option>
                            <option value="Partially">Partially Compliant</option>
                            <option value="N.A">Not Applicable</option>
                          </select>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label>Owner *</Label>
                            <Input
                              value={editingControl?.owner || ''}
                              onChange={(e) => setEditingControl({ ...editingControl, owner: e.target.value })}
                              placeholder="Assign owner..."
                              className="mt-1"
                              required
                            />
                          </div>
                          <div>
                            <Label>Closing Date *</Label>
                            <Input
                              type="date"
                              value={editingControl?.due_date || ''}
                              onChange={(e) => setEditingControl({ ...editingControl, due_date: e.target.value })}
                              className="mt-1"
                              required
                            />
                          </div>
                        </div>

                        <div>
                          <Label>Remediation Status</Label>
                          <select
                            value={editingControl?.remediation_status || 'Pending'}
                            onChange={(e) => setEditingControl({ ...editingControl, remediation_status: e.target.value })}
                            className="w-full border border-input rounded-sm px-3 py-2 text-sm mt-1"
                          >
                            <option value="Pending">Pending</option>
                            <option value="In Progress">In Progress</option>
                            <option value="Blocked">Blocked</option>
                            <option value="Under Review">Under Review</option>
                          </select>
                        </div>

                        <div>
                          <Label>Action Plan *</Label>
                          <Textarea
                            value={editingControl?.action_plan || ''}
                            onChange={(e) => setEditingControl({ ...editingControl, action_plan: e.target.value })}
                            placeholder="Describe the remediation plan..."
                            rows={3}
                            className="mt-1"
                            required
                          />
                        </div>

                        <div>
                          <Label>Evidence</Label>
                          <Textarea
                            value={editingControl?.evidence || ''}
                            onChange={(e) => setEditingControl({ ...editingControl, evidence: e.target.value })}
                            placeholder="Document evidence of implementation..."
                            rows={2}
                            className="mt-1"
                          />
                        </div>

                        <div className="flex gap-2 pt-4">
                          <Button type="submit" className="flex-1">Update Control</Button>
                          <Button type="button" variant="outline" onClick={() => {
                            setShowDialog(false);
                            setEditingControl(null);
                          }}>
                            Cancel
                          </Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
